import { useCallback, useState } from "react";
import { Api } from "services/api/api.service";
import type { Result } from "utils/result";
import type { TimeReportErrorKey } from "hooks/timeReportErrors";
import { resolveAlertKey } from "hooks/timeReportErrors";

export interface WithdrawTimeReportPayload {
  companyId: number;
  timeReportId: number;
  companyNotes: string;
}

export interface UseWithdrawTimeReportResult {
  withdrawTimeReport: (
    payload: WithdrawTimeReportPayload,
  ) => Promise<Result<void, TimeReportErrorKey>>;
  isLoading: boolean;
  error: TimeReportErrorKey | null;
}

interface ApiErrorPayload {
  errorMessage?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const resolveApiErrorMessage = (error: unknown): string => {
  if (!isRecord(error)) return "Unexpected error";
  const response = isRecord(error.response) ? error.response : null;
  const data =
    response && isRecord(response.data)
      ? (response.data as ApiErrorPayload)
      : undefined;
  if (data?.errorMessage) return data.errorMessage;
  return "Unexpected error";
};

export const useWithdrawTimeReport = (): UseWithdrawTimeReportResult => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<TimeReportErrorKey | null>(null);

  const withdrawTimeReport = useCallback(
    async (
      payload: WithdrawTimeReportPayload,
    ): Promise<Result<void, TimeReportErrorKey>> => {
      setIsLoading(true);
      setError(null);
      try {
        await Api().company.timeReports.withdrawTimeReport(
          payload.companyId,
          payload.timeReportId,
          payload.companyNotes,
        );
        return { ok: true, value: undefined };
      } catch (err: unknown) {
        const message = resolveApiErrorMessage(err);
        const alertKey = resolveAlertKey(message);
        setError(alertKey);
        return { ok: false, error: alertKey };
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return { withdrawTimeReport, isLoading, error };
};
